import React, { FC, ChangeEvent } from 'react';
import {
  Box,
  TextField,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  SelectChangeEvent,
} from '@mui/material';
import { useRegistrationContext } from '../../contexts/RegistrationContext';
import DateInput from '../common/DateInput';

interface HealthInfoFormProps {
  onNext: () => void;
  onBack: () => void;
}

export const HealthInfoForm: FC<HealthInfoFormProps> = ({ onNext, onBack }) => {
  const { formData, updateField } = useRegistrationContext();

  const handleWeightChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const regex = /^[0-9\b]+$/;
    if (event.target.value === '' || regex.test(event.target.value)) {
      updateField('weight', event.target.value);
    }
  };

  const handleHeightChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const regex = /^[0-9\b]+$/;
    if (event.target.value === '' || regex.test(event.target.value)) {
      updateField('height', event.target.value);
    }
  };

  const handleBirthdayChange = (date: string | null) => {
    updateField('birthday', date);
  };

  const handleSexChange = (event: SelectChangeEvent<string>) => {
    updateField('sex', event.target.value as string);
  };

  const handleDiseaseChange = (
    disease: 'diabetes' | 'thrombosis' | 'heartDisease'
  ) => {
    updateField(disease, !formData[disease]);
  };

  const handleOtherDiseasesChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    updateField('otherDiseases', event.target.value);
  };

  const isFormValid = () => {
    return (
      formData.birthday !== null &&
      formData.sex !== '' &&
      formData.height !== '' &&
      formData.weight !== ''
    );
  };

  return (
    <Box sx={{ paddingX: '5%' }}>
      <Button
        onClick={onBack}
        size="small"
        sx={{ marginY: '2%', fontWeight: 'bold' }}
        variant="text"
      >
        {'← Atras'}
      </Button>
      <TextField
        value={formData.weight}
        onChange={handleWeightChange}
        sx={{ width: '100%', marginY: '2%' }}
        label="Peso"
        variant="outlined"
        inputProps={{ inputMode: 'numeric' }}
        InputProps={{
          endAdornment: <InputAdornment position="start">kg</InputAdornment>,
        }}
      />

      <TextField
        value={formData.height}
        onChange={handleHeightChange}
        sx={{ width: '100%', marginY: '2%' }}
        label="Altura"
        variant="outlined"
        inputProps={{ inputMode: 'numeric' }}
        InputProps={{
          endAdornment: <InputAdornment position="start">cm</InputAdornment>,
        }}
      />

      <p>Fecha de nacimiento</p>
      <DateInput onChange={handleBirthdayChange} />

      <FormControl sx={{ width: '100%', marginY: '2%' }}>
        <InputLabel id="sex-select-label">Sexo</InputLabel>
        <Select
          value={formData.sex}
          onChange={handleSexChange}
          labelId="sex-select-label"
          id="sex-select"
          label="Sexo"
          variant="outlined"
        >
          <MenuItem value="M">Hombre</MenuItem>
          <MenuItem value="F">Mujer</MenuItem>
          <MenuItem value="-">Prefiero no decir</MenuItem>
        </Select>
      </FormControl>

      <FormGroup>
        <p>
          Marque la casilla correspondiente si ha sido
          <b> diagnosticado por un médico</b> con alguna de las siguientes
          enfermedades
        </p>
        <FormControlLabel
          onChange={() => handleDiseaseChange('diabetes')}
          control={<Checkbox checked={formData.diabetes} />}
          label="Diabetes"
        />
        <FormControlLabel
          onChange={() => handleDiseaseChange('thrombosis')}
          control={<Checkbox checked={formData.thrombosis} />}
          label="Antecedentes de Trombosis"
        />
        <FormControlLabel
          onChange={() => handleDiseaseChange('heartDisease')}
          control={<Checkbox checked={formData.heartDisease} />}
          label="Antecedentes de Enfermedades Cardiovasculares"
        />
      </FormGroup>

      <FormControlLabel
        control={<Checkbox />}
        label="Ninguna de las anteriores"
      />
      <TextField
        value={formData.otherDiseases}
        onChange={handleOtherDiseasesChange}
        sx={{ width: '100%', marginY: '2%' }}
        label="Otras enfermedades o antecedentes"
        variant="outlined"
      />

      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={12}
      >
        <Button
          disabled={!isFormValid()}
          onClick={onNext}
          size="large"
          sx={{ marginY: '2%', fontWeight: 'bold', borderRadius: '50px' }}
          variant="contained"
        >
          {'Siguiente →'}
        </Button>
      </Stack>
    </Box>
  );
};
